import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { BookOpen, PenLine, Users, ScrollText, LogOut, Flame, Sparkles, BarChart3, Settings as SettingsIcon } from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const links = [
    { to: "/app", label: "Weeks", icon: BookOpen, testid: "nav-weeks" },
    { to: "/app/journal", label: "Journal", icon: PenLine, testid: "nav-journal" },
    { to: "/app/verses", label: "Verses", icon: Sparkles, testid: "nav-verses" },
    { to: "/app/groups", label: "Groups", icon: Users, testid: "nav-groups" },
    { to: "/app/testimony", label: "Testimony", icon: Flame, testid: "nav-testimony" },
    { to: "/app/covenant", label: "Covenant", icon: ScrollText, testid: "nav-covenant" },
    ...(user?.is_leader ? [{ to: "/app/stats", label: "Stats", icon: BarChart3, testid: "nav-stats" }] : []),
  ];
  return (
    <div className="min-h-screen parchment">
      <header className="border-b border-border bg-background/85 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/app" data-testid="brand-link" className="flex items-center gap-3">
            <span className="font-serif text-2xl tracking-tight">Answering the Call</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => {
              const active = loc.pathname === l.to || loc.pathname.startsWith(l.to + "/");
              return (
                <Link key={l.to} to={l.to} data-testid={l.testid}
                  className={`text-sm px-3 py-2 rounded-sm ${active ? "text-primary underline-serif" : "text-muted-foreground hover:text-foreground"}`}>
                  {l.label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-muted-foreground">{user?.name}</span>
            <Link to="/app/settings" data-testid="nav-settings" className="text-muted-foreground hover:text-foreground" aria-label="Settings">
              <SettingsIcon className="w-4 h-4" />
            </Link>
            <Button variant="ghost" size="sm" data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }}>
              <LogOut className="w-4 h-4 mr-1" /> Sign out
            </Button>
          </div>
        </div>
        <div className="md:hidden border-t border-border overflow-x-auto">
          <div className="flex gap-1 px-4 py-2">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`m-${l.testid}`}
                className="text-xs px-2 py-1 whitespace-nowrap text-muted-foreground">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">{children}</main>
      <footer className="border-t border-border mt-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 text-xs text-muted-foreground flex flex-col sm:flex-row justify-between gap-2">
          <div>Answering the Call — an eleven-week disciplemaking training program.</div>
          <div className="font-serif italic">Trust · Follow · Imitate · Teach Others</div>
        </div>
      </footer>
    </div>
  );
}
